import AddTaskIcon from '@mui/icons-material/AddTask';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { API } from 'aws-amplify';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { AgentW9 } from '../../components/agent-w9';
import { Agreement } from '../../components/agreement';
import { RouteContainer } from '../../components/route-container';
import { AuthContext } from '../../contexts/auth-context';
import { useGettingStarted } from '../../hooks/use-getting-started';
import { AnnouncementData, NotificationData } from '../../types';
import { captureError } from '../../utils/capture-error';
import { isAgt } from '../../utils/is-agent';
import { MultiFactorInfo } from '../account/multi-factor-info';
import { ActionItems } from './action-items';
import { Announcements } from './announcements';
import { GettingStarted } from './getting-started';
import { Greeting } from './greeting';
import { Notifications } from './notifications';

export function Home() {
  // Context
  const {
    state: { user, impersonatedAgent, session },
  } = React.useContext(AuthContext);
  // State
  const [viewAgree, setViewAgree] = React.useState(false);
  const [viewTaxDoc, setViewTaxDoc] = React.useState(false);
  // Hooks
  const [searchParams, setSearchParams] = useSearchParams();

  // The App Bar's Getting started panel links here to open the Agent
  // Agreement or W-9 dialog
  const openParam = searchParams.get('open');
  React.useEffect(() => {
    if (openParam === 'agreement' || openParam === 'w9') {
      if (openParam === 'agreement') {
        setViewAgree(true);
      } else {
        setViewTaxDoc(true);
      }
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.delete('open');
          return next;
        },
        { replace: true },
      );
    }
  }, [openParam, setSearchParams]);

  const staffAgtNo = session?.getAccessToken().decodePayload()['myStaffForAgt'];

  const AgtNo = impersonatedAgent?.AgtNo || staffAgtNo || user?.getUsername();
  // Query - Action Items
  const pathAction = '/agent/action-items';
  const queryAction = useQuery({
    enabled: isAgt(AgtNo),
    queryKey: [pathAction, { AgtNo }],
    queryFn: async () => {
      if (isAgt(AgtNo)) {
        const response = await API.post('ContractingAPI', pathAction, {
          body: { AgtNo },
        });

        if (response.data) {
          return response.data;
        }
      }

      return null;
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Getting started
  const { query: queryStarted } = useGettingStarted();

  // Query - Notifications
  const pathNotifications = '/notifications/current';
  const queryNotifications = useQuery({
    enabled: isAgt(AgtNo),
    queryKey: [pathNotifications, { AgtNo }],
    queryFn: async () => {
      const response: {
        data: NotificationData[];
      } = await API.post('ContractingAPI', pathNotifications, {
        body: { AgtNo },
      });

      if (response.data) {
        return response.data;
      }

      return [];
    },
    onError: (error) => captureError({ data: { error } }),
  });

  // Query - Announcements
  const pathAnnouncements = '/announcements';
  const queryAnnouncements = useQuery({
    queryKey: [pathAnnouncements],
    queryFn: async () => {
      const response: {
        data: AnnouncementData[];
      } = await API.post('ContractingAPI', pathAnnouncements, {});

      if (response.data) {
        return response.data;
      }

      return [];
    },
    onError: (error) => captureError({ data: { error } }),
  });

  const displayGettingStarted = isAgt(AgtNo);

  const isLoading =
    Boolean(queryStarted.isLoading && queryStarted.fetchStatus !== 'idle') ||
    Boolean(queryAction.isLoading && queryAction.fetchStatus !== 'idle');

  return (
    <RouteContainer
      routeTitle="Home"
      withContainer
      maxWidth={displayGettingStarted ? 'xl' : 'md'}
      loading={isLoading}
      hasError={queryStarted.isError || queryAction.isError}
    >
      <Box sx={{ pt: 2, pb: 16 }}>
        <Agreement
          open={viewAgree}
          hideIfCompleted
          onClose={() => setViewAgree(false)}
        />

        <AgentW9
          open={viewTaxDoc}
          hideIfCompleted
          onClose={() => setViewTaxDoc(false)}
        />

        {user && staffAgtNo ? (
          <Box sx={{ mb: 2 }}>
            <MultiFactorInfo user={user} />{' '}
          </Box>
        ) : null}

        <Stack spacing={2} direction={{ xs: 'column', md: 'row' }}>
          <Box flex={1.5}>
            <Stack spacing={2}>
              <Box sx={{ px: 1, display: 'flex' }}>
                <Box sx={{ transform: 'rotate(-12deg)', pr: 2 }}>
                  <AddTaskIcon fontSize="large" />
                </Box>

                <Typography variant="h4" component="div">
                  Your Dashboard
                </Typography>
              </Box>

              <Greeting />

              <ActionItems data={queryAction.data || []} />

              <Notifications data={queryNotifications.data || []} />

              <Announcements data={queryAnnouncements.data || []} />
            </Stack>
          </Box>{' '}
          {displayGettingStarted && queryStarted.data ? (
            <Box flex={1}>
              <GettingStarted
                data={queryStarted.data}
                onViewAgree={() => setViewAgree(true)}
                onViewTaxDoc={() => setViewTaxDoc(true)}
              />
            </Box>
          ) : null}
        </Stack>
      </Box>
    </RouteContainer>
  );
}
